import type { GroupNode, LayerConfig, TreeNode } from '../../api/types';
import { groupKey, inZoomRange, isGroup } from '../tree';

/**
 * The Layers tab as a flat list of rows: groups and layers in panel order, each with its depth. Only rows inside open
 * groups are listed, so a deep tree of hundreds of layers renders only what is on screen.
 */

export interface GroupRow {
  kind: 'group';
  /** React key: the node's path, unique even when a layer is listed twice. */
  path: string;
  /** Visibility key (`group:<id>`). */
  key: string;
  id: string;
  title: string;
  depth: number;
  open: boolean;
  /** Every layer id under the group, for Zoom to and the labels toggle. */
  layerIds: string[];
}

export interface LayerRow {
  kind: 'layer';
  path: string;
  /** Visibility key (the layer id). */
  key: string;
  id: string;
  title: string;
  depth: number;
  /** False when the current zoom is outside the layer's range: the row is greyed. */
  inScale: boolean;
}

export type Row = GroupRow | LayerRow;

/** The hint on a row that is out of scale range (our wording, not the SDK layer list's). */
export const OUT_OF_SCALE_HINT = 'Not shown at this scale';

export function layerIdsUnder(nodes: readonly TreeNode[]): string[] {
  return nodes.flatMap((n) => (isGroup(n) ? layerIdsUnder(n.children) : [n.layer]));
}

/** Groups the config ships open (production opens its named groups the same way). */
export function defaultOpenGroups(tree: readonly TreeNode[]): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  const walk = (nodes: readonly TreeNode[]): void => {
    for (const n of nodes) {
      if (!isGroup(n)) continue;
      out[groupKey(n.group)] = n.open ?? false;
      walk(n.children);
    }
  };
  walk(tree);
  return out;
}

const normalise = (s: string): string => s.toLocaleLowerCase().trim();

export interface RowOptions {
  /** Open state per group key; groups not listed use their config default. */
  open: Readonly<Record<string, boolean>>;
  /** Search text. While searching every group opens, and only matches (with their ancestors) are listed. */
  query: string;
  zoom: number;
}

export function visibleRows(
  tree: readonly TreeNode[],
  layers: ReadonlyMap<string, Pick<LayerConfig, 'title' | 'zoom'>>,
  opts: RowOptions,
): Row[] {
  const q = normalise(opts.query);
  const searching = q.length > 0;
  const out: Row[] = [];

  const titleOf = (n: TreeNode): string => (isGroup(n) ? n.title : (layers.get(n.layer)?.title ?? n.layer));
  const matches = (n: TreeNode): boolean => normalise(titleOf(n)).includes(q);
  // A node is listed while searching if it matches, or anything under it matches.
  const hasMatch = (n: TreeNode): boolean => matches(n) || (isGroup(n) && n.children.some(hasMatch));

  const walk = (nodes: readonly TreeNode[], depth: number, parentPath: string, showAll: boolean): void => {
    for (const n of nodes) {
      if (searching && !showAll && !hasMatch(n)) continue;
      if (isGroup(n)) {
        const key = groupKey(n.group);
        const path = `${parentPath}/${key}`;
        const open = searching ? true : (opts.open[key] ?? n.open ?? false);
        out.push(groupRow(n, key, path, depth, open));
        // A group whose own title matches shows everything in it.
        if (open) walk(n.children, depth + 1, path, showAll || (searching && matches(n)));
      } else {
        const layer = layers.get(n.layer);
        if (!layer) continue;
        out.push({
          kind: 'layer',
          path: `${parentPath}/${n.layer}`,
          key: n.layer,
          id: n.layer,
          title: layer.title,
          depth,
          inScale: inZoomRange(opts.zoom, layer.zoom),
        });
      }
    }
  };
  walk(tree, 0, '', false);
  return out;
}

function groupRow(n: GroupNode, key: string, path: string, depth: number, open: boolean): GroupRow {
  return { kind: 'group', path, key, id: n.group, title: n.title, depth, open, layerIds: layerIdsUnder(n.children) };
}

/** Number of layers a search matches (shown when nothing is listed). */
export function countMatches(
  tree: readonly TreeNode[],
  layers: ReadonlyMap<string, Pick<LayerConfig, 'title'>>,
  query: string,
): number {
  const q = normalise(query);
  if (!q) return 0;
  return layerIdsUnder(tree).filter((id) => normalise(layers.get(id)?.title ?? id).includes(q)).length;
}
